import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import {
	Permissions,
	TeamActionActions,
	TeamActionDocument,
	TeamActionSubscription,
} from '@/graphql';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';

import { useAuth } from '../AuthProvider';
import { useEnvironment } from '../EnvironmentProvider';
import { usePermissions } from '../PermissionsProvider';
import { useServices } from '../ServicesProvider';
import { useSetlists } from '../SetlistsProvider';
import { useTeam } from '../TeamProvider';
import { useTeamSongs } from '../TeamSongsProvider';
import { useUnreadChats } from '../UnreadChatsProvider/UnreadChatsProvider';

export interface TeamActionSubscriptionContext {
	actions: {
		action: TeamActionActions;
		resourceIds: string[];
		actorId: string;
	}[];
	log: string[];
	isConnected: boolean;
	setChange: React.Dispatch<React.SetStateAction<number>>;
}

const defaultValue: TeamActionSubscriptionContext = {
	actions: [],
	log: [],
	isConnected: false,
	setChange: () => null,
};

const TeamActionSubscriptionContext =
	createContext<TeamActionSubscriptionContext>(defaultValue);

export function useTeamActionSubscription(): TeamActionSubscriptionContext {
	const context = useContext(TeamActionSubscriptionContext);
	if (isNil(context)) {
		throw new Error(`useTeam must be used within a TeamProvider`);
	}

	return context;
}

/*

## Subscribe
https://github.com/enisdenjo/graphql-ws/blob/master/PROTOCOL.md

`Direction: Client -> Server`

Requests an operation specified in the message payload. This message provides a unique ID
field to connect published messages to the operation requested by this message.

If there is already an active subscriber for an operation matching the provided ID,
regardless of the operation type, the server must close the socket immediately with the
event 4409: Subscriber for <unique-operation-id> already exists.

The server needs only keep track of IDs for as long as the subscription is active.
Once a client completes an operation, it is free to re-use that ID.

*/

export type GraphQlTransportWsNextOperation<T extends Object> = {
	id: string;
	type: 'next';
	payload: {
		data: T;
	};
};

class PingPongRetry {
	pingId: number;
	pongTimeout?: NodeJS.Timeout;
	waitForPongTimeout: number;

	constructor(waitForPongTimeout?: number) {
		this.pingId = 0;
		this.waitForPongTimeout = waitForPongTimeout || 5000;
	}

	private _getPingId(userId) {
		return `${userId}-team-action-subscription-ping-${this.pingId}`;
	}

	sendPing(ws: WebSocket, userId: string, onNoResponseCallback: () => void) {
		this.pingId = this.pingId + 1;

		ws.send(
			JSON.stringify({
				id: this._getPingId(userId),
				type: 'subscribe',
				payload: {
					operationName: 'ping',
					query: `query ping {
            ping
          }`,
				},
			}),
		);
		console.log(
			`[WS Client > PingPongRetry > ${this._getPingId(
				userId,
			)}] Sent a ping`,
		);
		this.pongTimeout = setTimeout(() => {
			console.log(
				`[WS Client > PingPongRetry > ${this._getPingId(
					userId,
				)}] Did not get a pong, calling onNoResponseCallback()`,
			);
			onNoResponseCallback();
		}, this.waitForPongTimeout);
	}

	checkPong(messageId: string, userId: string, onSuccess: () => void) {
		if (messageId === this._getPingId(userId)) {
			console.log(
				`[WS Client > PingPongRetry > ${this._getPingId(
					userId,
				)}] Received a pong`,
			);
			onSuccess();
			clearTimeout(this.pongTimeout);
			return true;
		}
		return false;
	}
}

// TODO: Improve this provider by implementing a mechanism for retrying on disconnect
// https://stackoverflow.com/a/62769990/3486304
export const TeamActionSubscriptionProvider: React.FC = ({ children }) => {
	const { token, user } = useAuth();
	const { refetch: refetchSetlists } = useSetlists();
	const { refetch: refetchPermissions } = usePermissions();
	const { refetch: refetchTeam } = useTeam();
	const {
		refetchChats: refetchUnreadChats,
		refetchVideoChats: refetchUnreadVideoChats,
	} = useUnreadChats();
	const { refetch: refetchServices } = useServices();
	const { api } = useEnvironment();
	const queryClient = useQueryClient();
	const { refetch: refetchTeamSongs } = useTeamSongs();
	const { enqueueSnackbar } = useSnackbar();
	const { hasPermission } = usePermissions();

	const clientRef = useRef<WebSocket | null>(null);
	const pingPongInterval = useRef<NodeJS.Timeout | null>(null);
	const pingPongRetry = useRef(new PingPongRetry());
	const [waitingToReconnect, setWaitingToReconnect] = useState(false);
	const [actions, setActions] = useState(defaultValue.actions);
	const [log, setLog] = useState(defaultValue.log);
	const [isConnected, setIsConnected] = useState(defaultValue.isConnected);
	const [change, setChange] = useState(0);

	useEffect(() => {
		if (waitingToReconnect) {
			return;
		}

		// Only set up the websocket once
		if (!clientRef.current) {
			const url = `${api.wsUrl}/subscriptions`;
			const ws = new WebSocket(url, 'graphql-transport-ws');
			console.log('[WS Client] Created');
			clientRef.current = ws;

			ws.onopen = () => {
				console.log('[WS Client] Connected');
				setLog((log) => [
					...log,
					`${[format(new Date(), 'HH:mm:ss')]} Connected`,
				]);
				setIsConnected(true);

				ws.send(
					JSON.stringify({
						type: 'connection_init',
						payload: { token },
					}),
				);
				ws.send(
					JSON.stringify({
						id: `${user.id}-team-action-subscription`,
						type: 'subscribe',
						payload: {
							// variables: { welderId: 1 },
							operationName: 'teamAction',
							query: TeamActionDocument,
						},
					}),
				);
				pingPongInterval.current = setInterval(() => {
					setLog((log) => [
						...log,
						`${[format(new Date(), 'HH:mm:ss')]} Sent a ping`,
					]);

					pingPongRetry.current.sendPing(ws, user.id, () => {
						setLog((log) => [
							...log,
							`${[
								format(new Date(), 'HH:mm:ss'),
							]} Did not receive a pong`,
						]);

						setIsConnected(false);
						// Will cause the effect's cleanup to run and delete the socket instance
						// this will also delete the interval
						setWaitingToReconnect(true);
						setTimeout(() => {
							console.log('[WS Client] Attepting a reconnect');
							// then re-run the effect again to re-create a new one socket instance
							// which would try the connection again
							setWaitingToReconnect(false);
						}, 100);
					});
				}, 20_000);
			};

			ws.onmessage = (event) => {
				const msg: GraphQlTransportWsNextOperation<TeamActionSubscription> =
					JSON.parse(event.data);
				// We are using an optional ? chain here just in case the message
				// does not have any of these properties.

				// If the message is a response (a pong) for the last ping, then it will
				// clear the interval that is currently ticking for it and it won't
				// fire the onNoResponseCallback
				pingPongRetry.current.checkPong(msg.id, user.id, () => {
					setLog((log) => [
						...log,
						`${[format(new Date(), 'HH:mm:ss')]} Received a pong`,
					]);
				});

				// Note that the user who triggered the action (for example TeamSongUpdated) won't
				// receive a WS message as they are the actor.
				// Therefore, as an example, an update to the Team Songs should trigger a refetch or invalidation locally
				// for the user. All other users' Team Songs will be refetched due to receiving a WS message.
				if (msg.payload?.data?.teamAction?.action) {
					// Add the incomming action to a list of the last N (5 in the current case) actions
					setActions((state) => {
						if (msg.payload.data.teamAction) {
							return [
								...state,
								{
									action: msg.payload.data.teamAction.action,
									resourceIds:
										msg.payload.data.teamAction
											?.resourceIds || [],
									actorId:
										msg.payload.data.teamAction.actorId,
								},
							].splice(-5);
						}
						return state;
					});
					switch (msg.payload.data.teamAction.action) {
						case TeamActionActions.TeamSongsChanged: {
							console.log(
								`[TeamActionSubscripton] Recieved ${TeamActionActions.TeamSongsChanged}, invalidating ListMyTeamSongs query`,
							);
							refetchTeamSongs();
							return;
						}
						case TeamActionActions.SetlistsChanged: {
							console.log(
								`[TeamActionSubscripton] Recieved ${TeamActionActions.SetlistsChanged}, refetching setlists`,
							);
							refetchSetlists();
							return;
						}
						case TeamActionActions.TeamChanged: {
							console.log(
								`[TeamActionSubscripton] Recieved ${TeamActionActions.TeamChanged}, refetching permissions and team`,
							);
							refetchPermissions();
							refetchTeam();
							return;
						}
						case TeamActionActions.ChatMessageSent:
						case TeamActionActions.ChatChanged: {
							console.log(
								`[TeamActionSubscripton] Recieved ${TeamActionActions.ChatChanged}, refetching global undread chats, invalidating chats`,
							);
							refetchUnreadChats();
							queryClient.invalidateQueries(['listMyChats']);
							return;
						}
						case TeamActionActions.VideoChatChanged: {
							console.log(
								`[TeamActionSubscripton] Recieved ${TeamActionActions.VideoChatChanged}, refetching global undread video chats, invalidating video chats`,
							);
							refetchUnreadVideoChats();
							queryClient.invalidateQueries([
								'ListTeamVideoChats',
							]);
							return;
						}
						case TeamActionActions.ServiceChanged: {
							console.log(
								`[TeamActionSubscripton] Recieved ${TeamActionActions.ServiceChanged}, refetching services`,
							);
							refetchServices();
							return;
						}
						// case TeamActionActions.MembershipDeleted: {
						// 	console.log(
						// 		`Recieved ${TeamActionActions.MembershipDeleted}, logging out`,
						// 	);
						// 	logout();
						// 	return;
						// }
					}
				}
			};

			ws.onerror = (event) => {
				console.log('WS ERROR', event);
			};

			ws.onclose = () => {
				setIsConnected(false);

				if (clientRef.current) {
					// Connection failed
					console.log('[WS Client] WS closed by server');
				} else {
					// Cleanup initiated from app side, can return here, to not attempt a reconnect
					console.log(
						'[WS Client] WS closed by app component cleanup',
					);

					return;
				}

				if (waitingToReconnect) {
					return;
				}

				// Parse event code and log
				setIsConnected(false);
				console.log('[WS Client] WS closed');
				console.log('[WS Client] Will try a reconnect in 5s');

				// Setting this will trigger a re-run of the effect,
				// cleaning up the current websocket, but not setting
				// up a new one right away
				setWaitingToReconnect(true);

				// This will trigger another re-run, and because it is false,
				// the socket will be set up again
				setTimeout(() => {
					console.log('[WS Client] Attepting a reconnect');
					setLog((log) => [
						...log,
						`${[
							format(new Date(), 'HH:mm:ss'),
						]} Attepting a reconnect`,
					]);
					setWaitingToReconnect(false);
				}, 5000);
			};

			return () => {
				// Unsubscribe before exit
				console.log('[WS Client] Cleanup');
				clearInterval(pingPongInterval.current || undefined);

				// Dereference, so it will set up next time
				clientRef.current = null;
				try {
					ws.send(JSON.stringify({ id: '1', type: 'complete' }));
				} catch (err) {
					console.log(
						'[WS Client] Error while sending complete message',
					);
				} finally {
					ws.close();
				}
			};
		}
	}, [
		queryClient,
		refetchSetlists,
		refetchPermissions,
		refetchTeam,
		refetchUnreadChats,
		refetchUnreadVideoChats,
		refetchTeamSongs,
		refetchServices,
		token,
		user.id,
		api.wsUrl,
		waitingToReconnect,
		hasPermission,
		enqueueSnackbar,
	]);

	return (
		<TeamActionSubscriptionContext.Provider
			value={{
				actions,
				log,
				isConnected,
				setChange,
			}}
		>
			{children}
		</TeamActionSubscriptionContext.Provider>
	);
};

export default TeamActionSubscriptionProvider;
